<template>
  <section class="page-metrics">
    <app-stat
      v-for="(item, index) in metrics"
      :key="index + item.title"
      :stat="item.value"
      :formatter="toInteger"
      :prefix="item.prefix || null"
      :suffix="item.suffix || null"
      :description="item.title"
      :background-color="item.backgroundColor || null"
      :color="item.color || null"
      :small="small"
      class="page-metrics__metric"
      :class="{
        'page-metrics__metric--clickable': item.filterKey,
        'page-metrics__metric--active': activeFilter === item.filterKey,
      }"
      @click="handleMetricClick(item)"
    >
      <span v-if="item.secondaryValue" class="app-stat__value app-stat__value--small">
        {{ item.secondaryValue }}
      </span>
    </app-stat>
  </section>
</template>

<script setup>
import AppStat from '@/components/AppStat.vue';
import { toInteger } from '@/utils/common';

defineProps({
  metrics: {
    type: [Array, Object],
    required: true,
  },
  small: Boolean,
  activeFilter: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['filter-change']);

const handleMetricClick = item => {
  if (item.filterKey) {
    emit('filter-change', item.filterKey);
  }
};
</script>

<style lang="scss" scoped>
.page-metrics {
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
    overflow-x: auto;
  }
}

.page-metrics__metric {
  flex: 1;
  height: auto;

  @media (max-width: 768px) {
    min-width: 15rem;
  }

  &--clickable {
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 2px solid transparent;
      border-radius: 4px;
      transition: border-color 0.2s ease-in-out;
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--depth-3);

      &::before {
        border-color: var(--palette-color-brand-primary);
      }
    }

    &:active {
      transform: translateY(0);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
    }

    &:hover::after {
      opacity: 1;
    }
  }

  &--active {
    &::before {
      border-color: var(--palette-color-brand-primary) !important;
    }

    box-shadow: var(--depth-3);

    &::after {
      content: '✓';
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: var(--palette-color-brand-primary);
      color: var(--palette-color-base-white);
      border-radius: 50%;
      width: 1.25rem;
      height: 1.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: bold;
      z-index: 2;
      opacity: 1;
    }
  }
}
</style>
